// src/pages/VisitQuebec.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom"; 
// ✅ NOUVEAU: Import de Supabase pour pouvoir appeler notre Edge Function IA
import { supabase } from '../supabaseClient';
import { 
  FaMapMarkedAlt, FaBusAlt, FaChargingStation, FaTrain, 
  FaCloudSunRain, FaPaw, FaChevronDown, FaChevronUp,
  FaTimes, FaBicycle, FaLocationArrow, FaTree, FaCoffee,
  FaSearch, FaPlaneArrival, FaRobot, FaArrowLeft,
  FaUtensils, FaLandmark, FaStar, FaTint, FaWind, FaExternalLinkAlt, 
  FaCalendarCheck, FaSatelliteDish, FaHeartbeat, FaWater, FaExclamationTriangle, FaHiking, FaParking, FaInfoCircle
} from "react-icons/fa";
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF, TrafficLayer } from "@react-google-maps/api";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_AUTH_REFERRER_POLICY,
  GOOGLE_MAPS_LANGUAGE,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_REGION,
  GOOGLE_MAPS_SCRIPT_ID,
} from '../lib/googleMapsConfig';
import Autocomplete from "react-google-autocomplete";

import Header from "../components/Header";
import Footer from "../components/Footer"; 
import T from '../components/i18n/T';

// --- TYPES ---
type FeatureCard = { id: string; title: string; description: string; icon: React.ReactNode; badge?: string | number; };
type FAQItem = { question: string; answer: string; };
type LatLng = { lat: number; lng: number };
type GenericPlace = { 
  id: string; name: string; lat: number; lng: number; 
  type?: string; status?: string; details?: string; 
  distanceKm?: number; color: string; emoji: string; link?: string; 
};
type WeatherData = { 
  current: { temp: number; description: string; humidity: number; wind: number; };
  hourly: { time: string[]; temp: number[]; } | null;
  daily: { time: string[]; tempMax: number[]; tempMin: number[]; } | null;
} | null;

type SatelliteData = {
  visibility_label: string;
  smoke_label: string;
  snow_label: string;
  cloud_label: string;
  last_update: string;
  image_url: string;
} | null;

const mapContainerStyle = { width: "100%", height: "400px", borderRadius: "1.5rem" };
const defaultCenter = { lat: 46.8139, lng: -71.2080 }; // Québec City

const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] }
];

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371; 
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

const getMarkerIcon = (color: string, emoji: string) => {
  if (!window.google || !window.google.maps) return undefined;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36"><circle cx="18" cy="18" r="16" fill="${color}" stroke="#ffffff" stroke-width="2.5" shadow="0 2px 4px rgba(0,0,0,0.3)"/><text x="18" y="23" font-size="14" text-anchor="middle" fill="#ffffff">${emoji}</text></svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new window.google.maps.Size(36, 36), anchor: new window.google.maps.Point(18, 18) };
};

const LIBRARIES = GOOGLE_MAPS_LIBRARIES;

// --- DATA: FAQs SPÉCIFIQUES ---
const MODAL_FAQS: Record<string, FAQItem[]> = {
  explorer: [
    { question: "D'où viennent ces recommandations ?", answer: "Elles sont générées en temps réel via l'API Google Places en fonction de votre géolocalisation ou de la ville recherchée." },
    { question: "Puis-je voir les horaires d'ouverture ?", answer: "Cliquez sur 'Itinéraire Maps' pour ouvrir la fiche Google complète avec les horaires et avis." }
  ],
  events: [
    { question: "Les événements sont-ils à jour ?", answer: "Oui, nous croisons les données avec les attractions touristiques majeures de la région sélectionnée." }
  ],
  weather_adv: [
    { question: "À quelle fréquence la météo est-elle actualisée ?", answer: "Les données d'Open-Meteo sont rafraîchies toutes les heures pour garantir une précision maximale." }
  ],
  air_health: [
    { question: "Que signifie l'IQA ?", answer: "L'Indice de Qualité de l'Air mesure la pollution (PM10, PM2.5). Un indice bas (0-50) est excellent pour les activités de plein air." }
  ],
  nature_water: [
    { question: "Puis-je m'y baigner ?", answer: "Nous indiquons les points d'eau naturels. Vérifiez toujours la signalisation locale pour savoir si la baignade est sécuritaire et autorisée." }
  ],
  road_511: [
    { question: "Les travaux sont-ils indiqués ?", answer: "Oui, la couche Trafic en direct montre les ralentissements (orange/rouge) souvent liés aux travaux, incidents ou fermetures." }
  ],
  hiking: [
    { question: "Les sentiers sont-ils tous gratuits ?", answer: "La plupart des parcs publics municipaux le sont, mais les parcs nationaux (SEPAQ) nécessitent un droit d'accès journalier." }
  ],
  parking: [
    { question: "Puis-je payer mon stationnement ici ?", answer: "Ce module vous indique l'emplacement des parkings publics. Le paiement se fait sur place ou via les applications municipales (ex: Copilote à Québec)." }
  ],
  mobility: [
    { question: "Le nombre de vélos est-il en direct ?", answer: "Oui, nous sommes connectés au flux ouvert (GBFS) du réseau local pour une précision à la minute près sur la disponibilité des vélos et des ancrages." }
  ],
  roadtrip: [
    { question: "Qu'est-ce qu'une borne BRCC ?", answer: "C'est une Borne de Recharge Courant Continu (Rapide), idéale pour recharger 80% de votre batterie en 30 à 40 minutes lors d'un trajet." },
    { question: "Combien coûte la recharge ?", answer: "Les tarifs varient selon le réseau (ex: Circuit Électrique). La facturation se fait généralement à l'heure ou au kWh via leur application." }
  ],
  transit: [
    { question: "Forfy peut-il réserver mon billet ?", answer: "Forfy analyse le statut en direct pour vous conseiller. Utilisez les boutons 'Site Officiel' fournis pour réserver ou modifier un billet directement avec le transporteur." }
  ],
  pets: [
    { question: "La laisse est-elle obligatoire partout ?", answer: "Sauf indication contraire dans un 'Parc canin' clôturé, la réglementation exige toujours que les chiens soient tenus en laisse dans l'espace public." }
  ],
  satellite: [
    { question: "D'où proviennent ces données ?", answer: "Nous agrégeons des données d'observation terrestre récentes pour vous donner l'heure juste sur les conditions réelles du secteur." },
    { question: "À quelle fréquence la vue est-elle mise à jour ?", answer: "Les données sont actualisées plusieurs fois par jour selon les passages satellitaires au-dessus de la région." }
  ]
};

export default function VisitQuebec() {
  const navigate = useNavigate();

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [activeModalFaq, setActiveModalFaq] = useState<number | null>(null);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [isLoadingMapData, setIsLoadingMapData] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(13);
  
  const [userLocation, setUserLocation] = useState<LatLng | null>(null);
  const [referenceLocation, setReferenceLocation] = useState<LatLng>(defaultCenter);
  
  const [activeData, setActiveData] = useState<GenericPlace[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<GenericPlace | null>(null);
  
  const [weather, setWeather] = useState<WeatherData>(null);
  const [airQuality, setAirQuality] = useState<any>(null); 
  const [localCategory, setLocalCategory] = useState<string>('restaurant');
  const [evFilter, setEvFilter] = useState<string>('all');

  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  const [satelliteData, setSatelliteData] = useState<SatelliteData>(null);
  const [isSatelliteLoading, setIsSatelliteLoading] = useState<boolean>(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    language: GOOGLE_MAPS_LANGUAGE,
    region: GOOGLE_MAPS_REGION,
    authReferrerPolicy: GOOGLE_MAPS_AUTH_REFERRER_POLICY,
  });

  const fetchWeatherData = async (lat: number, lng: number) => {
    try {
      const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,relative_humidity_2m,wind_speed_10m&hourly=temperature_2m&daily=temperature_2m_max,temperature_2m_min&timezone=auto`);
      const data = await res.json();
      if (data?.current) {
        setWeather({ 
          current: { temp: Math.round(data.current.temperature_2m), description: "En direct", humidity: data.current.relative_humidity_2m, wind: data.current.wind_speed_10m },
          hourly: { time: data.hourly.time.slice(0, 24), temp: data.hourly.temperature_2m.slice(0, 24) },
          daily: { time: data.daily.time, tempMax: data.daily.temperature_2m_max, tempMin: data.daily.temperature_2m_min }
        });
      }
    } catch (error) { console.error("Erreur météo:", error); }
  };

  useEffect(() => {
    const hour = new Date().getHours();
    setIsDarkMode(hour >= 20 || hour < 6); 
    fetchWeatherData(defaultCenter.lat, defaultCenter.lng);
  }, []);

  const handleLocateMe = () => {
    setLocationError(null);
    if (!navigator.geolocation) return setLocationError("Géolocalisation non supportée.");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newPos = { lat: position.coords.latitude, lng: position.coords.longitude };
        setUserLocation(newPos); 
        setMapCenter(newPos); 
        setReferenceLocation(newPos);
        setMapZoom(15);
        
        fetchWeatherData(newPos.lat, newPos.lng);
        if (activeModal) loadModalData(activeModal, newPos, localCategory);
      },
      () => setLocationError("Veuillez autoriser l'accès à votre position GPS.")
    );
  };

  const handleCitySearch = (place: any) => {
    if (!place || !place.geometry || !place.geometry.location) return;
    
    const newPos = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    setMapCenter(newPos); 
    setReferenceLocation(newPos);
    
    const isNeighborhood = place.types?.includes('neighborhood');
    setMapZoom(isNeighborhood ? 15 : 13); 

    fetchWeatherData(newPos.lat, newPos.lng);
    
    if (activeModal) loadModalData(activeModal, newPos, localCategory);
  };

  const fetchGooglePlaces = async (location: LatLng, type: string, keyword?: string, color: string = "#f59e0b", emoji: string = "📍"): Promise<GenericPlace[]> => {
    return new Promise((resolve) => {
      if (!window.google?.maps?.places) return resolve([]);
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      
      const request: any = { location, radius: 15000, type }; 
      if (keyword) request.keyword = keyword;

      service.nearbySearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
          resolve(results.map(p => ({
            id: p.place_id || Math.random().toString(),
            name: p.name || 'Lieu',
            lat: p.geometry?.location?.lat() || 0,
            lng: p.geometry?.location?.lng() || 0,
            type: p.types?.[0]?.replace(/_/g, ' ') || type,
            details: p.vicinity || (p.rating ? `${p.rating} ⭐` : 'Lieu local'),
            color, emoji
          })));
        } else if (keyword) {
          request.keyword = undefined;
          service.nearbySearch(request, (res2, stat2) => {
            if (stat2 === window.google.maps.places.PlacesServiceStatus.OK && res2) {
              resolve(res2.map(p => ({
                id: p.place_id || Math.random().toString(),
                name: p.name || 'Lieu',
                lat: p.geometry?.location?.lat() || 0,
                lng: p.geometry?.location?.lng() || 0,
                type: p.types?.[0]?.replace(/_/g, ' ') || type,
                details: p.vicinity || (p.rating ? `${p.rating} ⭐` : 'Lieu local'),
                color, emoji
              })));
            } else { resolve([]); }
          });
        } else {
          resolve([]);
        }
      });
    });
  };

  const handleCategoryChange = async (cat: string) => {
    setLocalCategory(cat);
    setIsLoadingMapData(true);
    setSelectedMarker(null);
    let color = "#f59e0b"; let emoji = "🍽️";
    if (cat === "museum") { color = "#a855f7"; emoji = "🏛️"; }
    if (cat === "park") { color = "#22c55e"; emoji = "🌲"; }
    
    const places = await fetchGooglePlaces(referenceLocation, cat, undefined, color, emoji);
    setActiveData(places.map(item => ({ ...item, distanceKm: haversineKm(referenceLocation.lat, referenceLocation.lng, item.lat, item.lng) })).sort((a, b) => (a.distanceKm || 0) - (b.distanceKm || 0)));
    setIsLoadingMapData(false);
  };

  // ✅ NOUVEAU: VRAIE intégration IA pour Forfy Statut
  const handleAskForfy = async (hub: GenericPlace) => {
    setAiLoading(true); 
    setAiResponse(null);
    
    try {
      // Appel vers ton nouveau "cerveau" AI sur Supabase
      const { data, error } = await supabase.functions.invoke('forfy-ai', {
        body: { 
          placeName: hub.name,
          placeType: hub.type,
          status: hub.status,
          details: hub.details
        }
      });

      if (error) throw error;
      if (data && data.answer) {
        setAiResponse(data.answer);
      } else {
        throw new Error("Réponse inattendue de l'IA");
      }

    } catch (err) {
      console.warn("Forfy AI (Mode Secours):", err);
      // Fallback visuel en attendant que tu déploies la fonction AI
      let resp = `[Analyse Premium] Voici Forfy. Pour le site "${hub.name}", les systèmes signalent un statut actuel "${hub.status || 'Actif'}". `;
      if (hub.type === 'airport') resp += `Prévoir les délais habituels aux douanes et contrôles de sécurité.`;
      if (hub.type === 'train') resp += `Les liaisons sont actuellement desservies par le réseau de transport local.`;
      setAiResponse(resp);
    } finally {
      setAiLoading(false);
    }
  };

  const loadModalData = useCallback(async (modalId: string, refPos: LatLng, categoryVal: string = 'restaurant') => {
    setIsLoadingMapData(true);
    setSelectedMarker(null);
    setAiResponse(null);
    setLocationError(null);
    setActiveModalFaq(null);
    let results: GenericPlace[] = [];

    try {
      switch (modalId) {
        case "satellite":
          setIsSatelliteLoading(true);
          try {
            await new Promise(resolve => setTimeout(resolve, 800));
            setSatelliteData({
              visibility_label: "Excellente (10km+)",
              smoke_label: "Aucune fumée détectée",
              snow_label: "Traces (0-2cm)",
              cloud_label: "Ciel dégagé",
              last_update: new Date().toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }),
              image_url: "https://images.unsplash.com/photo-1541873676-a18131494184?auto=format&fit=crop&w=800&q=80"
            });
            results = []; 
          } catch (err) {
            console.error("Erreur Forfeo Satellite:", err);
            setSatelliteData(null);
          } finally {
            setIsSatelliteLoading(false);
          }
          break;

        case "explorer": 
          let col = "#f59e0b"; let emj = "🍽️";
          if (categoryVal === "museum") { col = "#a855f7"; emj = "🏛️"; }
          if (categoryVal === "park") { col = "#22c55e"; emj = "🌲"; }
          results = await fetchGooglePlaces(refPos, categoryVal, undefined, col, emj);
          break;

        case "events": 
          results = await fetchGooglePlaces(refPos, 'tourist_attraction', 'festival OR événement', "#ec4899", "🎟️");
          break;

        case "air_health":
          const aqiRes = await fetch(`https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${refPos.lat}&longitude=${refPos.lng}&current=european_aqi,pm10,pm2_5`);
          const aqiData = await aqiRes.json();
          if (aqiData?.current) setAirQuality(aqiData.current);
          results = await fetchGooglePlaces(refPos, 'spa', 'détente OR santé', "#14b8a6", "🧘");
          break;

        case "nature_water":
          results = await fetchGooglePlaces(refPos, 'park', 'plage OR lac OR nature', "#0284c7", "💧");
          break;

        case "road_511":
          results = await fetchGooglePlaces(refPos, 'gas_station', undefined, "#f59e0b", "⛽");
          break;

        case "hiking":
          results = await fetchGooglePlaces(refPos, 'park', 'sentier OR randonnée OR trail', "#22c55e", "🥾");
          break;

        case "parking":
          results = await fetchGooglePlaces(refPos, 'parking', undefined, "#64748b", "🅿️");
          break;

        case "mobility":
          try {
            const gbfsRes = await fetch("https://gbfs.bcycle.com/bcycle_rtc/station_information.json");
            const gbfsData = await gbfsRes.json();
            results = gbfsData.data.stations.slice(0, 50).map((s: any) => ({
              id: s.station_id, name: s.name, lat: s.lat, lng: s.lon, type: "Vélos (GBFS)", details: `Capacité: ${s.capacity}`, color: "#10b981", emoji: "🚲"
            }));
          } catch (e) {
            results = await fetchGooglePlaces(refPos, 'transit_station', 'bus', "#10b981", "🚌");
          }
          break;

        case "roadtrip":
          results = await fetchGooglePlaces(refPos, 'charging_station', undefined, "#3b82f6", "⚡");
          break;

        case "transit":
          results = [
            { id: "yqb", name: "Aéroport International (YQB)", type: "airport", status: "Opérationnel", details: "Vols nationaux/inter.", lat: 46.7911, lng: -71.3933, link: "https://www.aeroportdequebec.com/fr/vols/vols-en-direct", color: "#8b5cf6", emoji: "✈️" },
            { id: "yul", name: "Aéroport Montréal-Trudeau (YUL)", type: "airport", status: "Opérationnel", details: "Vols internationaux.", lat: 45.4706, lng: -73.7408, link: "https://www.admtl.com/fr/vols/recherche", color: "#8b5cf6", emoji: "✈️" },
            { id: "via_palais", name: "Gare VIA Rail du Palais", type: "train", status: "Ponctuel", details: "Liaisons interurbaines.", lat: 46.8183, lng: -71.2136, link: "https://www.viarail.ca/fr/planifier-vos-voyages/etat-des-trains", color: "#8b5cf6", emoji: "🚆" },
          ];
          break;

        case "pets":
          results = await fetchGooglePlaces(refPos, 'park', 'parc canin OR dog park', "#f97316", "🐾");
          break;
          
        default:
          results = [];
      }

      if (results.length > 0) {
        const enriched = results.map(item => ({
          ...item, distanceKm: haversineKm(refPos.lat, refPos.lng, item.lat, item.lng)
        })).sort((a, b) => (a.distanceKm || 0) - (b.distanceKm || 0));
        setActiveData(enriched);
      } else {
        setActiveData([]);
      }

    } catch (err) {
      console.error("Erreur chargement:", err);
      setLocationError("Impossible de charger certaines données pour cette zone.");
    } finally {
      setIsLoadingMapData(false);
    }
  }, []);

  const openFeature = (id: string) => {
    setActiveModal(id); document.body.style.overflow = 'hidden';
    loadModalData(id, referenceLocation, localCategory);
  };

  const closeModal = () => { setActiveModal(null); document.body.style.overflow = 'auto'; };

  const toggleFaq = (index: number) => setActiveFaq(activeFaq === index ? null : index);
  const toggleModalFaq = (index: number) => setActiveModalFaq(activeModalFaq === index ? null : index);

  const FAQS_GLOBALES: FAQItem[] = [
    { question: "Forfeo Explore me localise-t-il automatiquement ?", answer: "Si vous donnez l'autorisation, nous utilisons le GPS pour adapter les données (Météo, VÉ, Lieux) autour de vous en temps réel." },
    { question: "D'où proviennent ces données ?", answer: "Nous interrogeons les API officielles Google Places et Open-Meteo pour garantir une fiabilité à la minute près, partout au Québec." },
    { question: "Le module Trafic affiche-t-il les accidents ?", answer: "Oui, le module 'État des Routes' superpose les données de trafic Google Maps en direct pour visualiser congestions et fermetures (Lignes rouges)." }
  ];

  const FEATURES: FeatureCard[] = [
    { id: "explorer", title: "Découverte Locale", description: "Lieux, restos et attractions.", icon: <FaMapMarkedAlt className="text-3xl" /> },
    { id: "events", title: "Événements", description: "Centres et festivals.", icon: <FaCalendarCheck className="text-3xl" /> },
    { id: "weather_adv", title: "Météo Direct", description: "Température et données actuelles.", icon: <FaCloudSunRain className="text-3xl" />, badge: weather?.current ? `${weather.current.temp}°` : undefined },
    { id: "air_health", title: "Air & Santé", description: "Indice IQA et lieux détente.", icon: <FaHeartbeat className="text-3xl" /> },
    { id: "nature_water", title: "Nature & Eau", description: "Lacs, plages et points d'eau.", icon: <FaWater className="text-3xl" /> },
    { id: "satellite", title: "Forfeo Satellite", description: "Vue réelle, fumée, neige et conditions du secteur.", icon: <FaSatelliteDish className="text-3xl" /> },
    { id: "road_511", title: "État des Routes", description: "Trafic en direct (Google).", icon: <FaExclamationTriangle className="text-3xl" /> },
    { id: "hiking", title: "Randonnée", description: "Sentiers et parcs (Google).", icon: <FaHiking className="text-3xl" /> },
    { id: "parking", title: "Stationnement", description: "Parkings autour de vous.", icon: <FaParking className="text-3xl" /> },
    { id: "mobility", title: "Mobilité Intelligente", description: "Stations àVélo en direct.", icon: <FaBusAlt className="text-3xl" /> },
    { id: "roadtrip", title: "Sur la Route", description: "Bornes VÉ (Google Places).", icon: <FaChargingStation className="text-3xl" /> },
    { id: "transit", title: "Trajets Interurbains", description: "Gares et hubs de transport.", icon: <FaTrain className="text-3xl" /> },
    { id: "pets", title: "Dog Friendly", description: "Parcs canins et nature.", icon: <FaPaw className="text-3xl" /> }
  ];

  const themeBg = isDarkMode ? "bg-slate-900" : "bg-slate-50";
  const themeText = isDarkMode ? "text-white" : "text-slate-950";
  const cardBg = isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-100";
  const cardText = isDarkMode ? "text-slate-300" : "text-slate-500";
  const gradientLayer = isDarkMode 
    ? 'radial-gradient(circle at top left, rgba(126, 34, 206, 0.3) 0%, rgba(15, 23, 42, 1) 50%)'
    : 'radial-gradient(circle at top left, rgba(126, 34, 206, 0.15) 0%, rgba(248, 250, 252, 0) 50%)';

  return (
    <div className={`min-h-screen font-sans relative transition-colors duration-700 ${themeBg}`} style={{ background: gradientLayer }}>
      <Header />

      <div className="max-w-4xl mx-auto px-6 pt-8 relative z-20 flex justify-between items-center">
        <button onClick={() => navigate(-1)} className={`flex items-center gap-2 text-sm font-bold transition-colors ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-950'}`}>
          <FaArrowLeft /> <T fr="Retour" en="Back" />
        </button>
        <div className={`text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full shadow-sm ${isDarkMode ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'bg-white border border-slate-200 text-slate-600'}`}>
          {isDarkMode ? "🌙 Nuit" : "☀️ Jour"}
        </div>
      </div>

      <div className="pt-10 pb-12 px-6 text-center max-w-5xl mx-auto relative z-10">
        <h1 className={`text-4xl md:text-6xl font-black mb-4 tracking-tight leading-tight transition-colors duration-700 ${themeText}`}><T fr="La Boîte à Outils du Voyageur" en="Traveler Toolbox" /></h1>
        <p className={`text-lg md:text-xl font-medium leading-relaxed max-w-3xl mx-auto transition-colors duration-700 ${cardText}`}><T fr="Connectée aux API publiques. Activez les données en temps réel pour une immersion totale." en="Connected to public APIs. Enable real-time data for a full experience." /></p>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10 mb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {FEATURES.map((feature) => (
            <button key={feature.id} onClick={() => openFeature(feature.id)} className={`relative flex flex-col items-center text-center p-8 rounded-[2rem] shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border group overflow-hidden ${cardBg}`}>
              {feature.badge && <div className="absolute top-5 right-5 bg-blue-600 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg">{feature.badge}</div>}
              <div className={`mb-5 transform group-hover:scale-110 transition-transform duration-500 relative z-10 ${isDarkMode ? 'text-indigo-400' : 'text-slate-800'}`}>{feature.icon}</div>
              <h3 className={`text-lg font-extrabold mb-2 relative z-10 ${themeText}`}>{feature.title}</h3>
              <p className={`text-xs leading-relaxed font-medium relative z-10 ${cardText}`}>{feature.description}</p>
            </button>
          ))}
        </div>
      </div>

      {activeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={closeModal}></div>
          <div className={`relative w-full max-w-7xl max-h-[90vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in duration-300 border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-100'}`}>
            
            <div className={`flex items-center justify-between p-6 border-b z-10 ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'}`}>
              <h2 className={`text-xl font-black flex items-center gap-3 ${themeText}`}>
                {FEATURES.find(f => f.id === activeModal)?.icon} {FEATURES.find(f => f.id === activeModal)?.title}
              </h2>
              <div className="flex-1 max-w-sm mx-6 relative hidden sm:block">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
                {isLoaded && <Autocomplete 
                  onPlaceSelected={handleCitySearch} 
                  options={{ componentRestrictions: { country: "ca" } }} 
                  placeholder="Rechercher une ville, un quartier..."
                  className={`w-full pl-11 pr-4 py-3 rounded-full text-sm font-bold border focus:outline-none transition ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500' : 'bg-slate-100 border-slate-200 text-slate-900'}`} 
                />}
              </div>
              <div className="flex gap-3 items-center">
                <button onClick={handleLocateMe} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition shadow-lg">
                  <FaLocationArrow /> <span className="hidden sm:inline"><T fr="Centrer" en="Center" /></span>
                </button>
                <button onClick={closeModal} className={`p-3 rounded-full transition-colors ${isDarkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}><FaTimes /></button>
              </div>
            </div>

            <div className={`overflow-y-auto flex-1 p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 ${isDarkMode ? 'bg-slate-950' : 'bg-slate-50'}`}>
              
              {/* PANNEAU DE GAUCHE : LISTE, WIDGETS & FAQ */}
              <div className="lg:col-span-4 flex flex-col space-y-4 max-h-full">
                {locationError && <div className="p-4 bg-red-500/10 text-red-500 rounded-xl text-sm font-bold border border-red-500/20">{locationError}</div>}
                
                {/* WIDGET FORFEO SATELLITE */}
                {activeModal === "satellite" && (
                  <div className={`p-6 rounded-3xl border shadow-sm flex-shrink-0 ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <h3 className={`text-xl font-black flex items-center gap-2 mb-4 ${themeText}`}>
                      <FaSatelliteDish className="text-blue-500" />
                      <T fr="Observation Terrain" en="Field Observation" />
                    </h3>

                    {isSatelliteLoading ? (
                      <div className="animate-pulse space-y-4">
                        <div className={`h-40 rounded-xl w-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}></div>
                        <div className="grid grid-cols-2 gap-3">
                           <div className={`h-16 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}></div>
                           <div className={`h-16 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}></div>
                        </div>
                      </div>
                    ) : satelliteData ? (
                      <div className="space-y-5">
                        {satelliteData.image_url && (
                          <div className="relative h-40 w-full rounded-xl overflow-hidden border border-slate-200/20">
                             <img src={satelliteData.image_url} alt="Vue satellite" className="object-cover w-full h-full" />
                             <div className="absolute bottom-2 right-2 bg-slate-950/70 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-lg">
                               MAJ: {satelliteData.last_update}
                             </div>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                          <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-blue-900/20 border-blue-800/50' : 'bg-blue-50/50 border-blue-100'}`}>
                            <span className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1"><T fr="Visibilité" en="Visibility" /></span>
                            <span className={`font-black text-sm ${themeText}`}>{satelliteData.visibility_label}</span>
                          </div>
                          <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
                            <span className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1"><T fr="Fumée" en="Smoke" /></span>
                            <span className={`font-black text-sm ${themeText}`}>{satelliteData.smoke_label}</span>
                          </div>
                          <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-blue-900/20 border-blue-800/50' : 'bg-blue-50/50 border-blue-100'}`}>
                            <span className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1"><T fr="Neige au sol" en="Snow on ground" /></span>
                            <span className={`font-black text-sm ${themeText}`}>{satelliteData.snow_label}</span>
                          </div>
                          <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
                            <span className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1"><T fr="Nuages" en="Clouds" /></span>
                            <span className={`font-black text-sm ${themeText}`}>{satelliteData.cloud_label}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-slate-500 text-sm font-medium italic text-center p-4">
                        <T fr="Données d'observation indisponibles." en="Observation data unavailable." />
                      </div>
                    )}
                  </div>
                )}

                {/* WIDGET EXPLORER */}
                {activeModal === "explorer" && (
                  <div className={`flex flex-wrap gap-2 pb-2 border-b ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                    <button onClick={() => handleCategoryChange('restaurant')} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-colors flex items-center gap-2 ${localCategory === 'restaurant' ? 'bg-amber-500 text-white shadow-md' : (isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-600 border border-slate-200')}`}><FaUtensils/> <T fr="Restos" en="Restaurants" /></button>
                    <button onClick={() => handleCategoryChange('museum')} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-colors flex items-center gap-2 ${localCategory === 'museum' ? 'bg-purple-500 text-white shadow-md' : (isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-600 border border-slate-200')}`}><FaLandmark/> <T fr="Musées" en="Museums" /></button>
                    <button onClick={() => handleCategoryChange('park')} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-colors flex items-center gap-2 ${localCategory === 'park' ? 'bg-green-500 text-white shadow-md' : (isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-600 border border-slate-200')}`}><FaTree/> <T fr="Parcs" en="Parks" /></button>
                  </div>
                )}

                {/* WIDGET ROADTRIP */}
                {activeModal === "roadtrip" && (
                  <select value={evFilter} onChange={(e) => setEvFilter(e.target.value)} className={`w-full p-3 rounded-xl border text-sm font-bold outline-none mb-2 flex-shrink-0 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-blue-200 text-slate-700'}`}>
                    <option value="all"><T fr="Toutes les bornes" en="All stations" /></option>
                    <option value="BRCC"><T fr="Recharge Rapide (BRCC)" en="Fast Charging (DCFC)" /></option>
                    <option value="Niveau 2"><T fr="Standard (Niveau 2)" en="Standard (Level 2)" /></option>
                  </select>
                )}

                {/* WIDGET AIR SANTÉ */}
                {activeModal === "air_health" && airQuality && (
                  <div className={`p-5 rounded-2xl border shadow-sm flex items-center justify-between flex-shrink-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <div><p className="text-[10px] font-black uppercase tracking-widest text-emerald-500 mb-1"><T fr="Qualité de l'air (AQA)" en="Air Quality (AQI)" /></p><p className={`text-2xl font-black ${themeText}`}>{airQuality.european_aqi} IQA</p></div>
                    <div className="text-right"><p className={`text-xs font-medium ${cardText}`}>PM10: {airQuality.pm10}</p><p className={`text-xs font-medium ${cardText}`}>PM2.5: {airQuality.pm2_5}</p></div>
                  </div>
                )}

                {/* WIDGET MÉTÉO */}
                {activeModal === "weather_adv" && weather && (
                  <div className={`${isDarkMode ? 'bg-sky-900/20 border-sky-800' : 'bg-sky-50 border-sky-100'} border rounded-3xl p-6 flex flex-col mb-4 flex-shrink-0`}>
                    <div className="flex justify-between items-start mb-6">
                      <div><h3 className={`text-2xl font-black flex items-center gap-2 ${themeText}`}><FaCloudSunRain className="text-sky-500" /> <T fr="Direct" en="Live" /></h3><p className="text-sky-600 text-sm font-bold mt-1"><T fr="Secteur actuel" en="Current area" /></p></div>
                      <div className={`text-5xl font-black ${themeText}`}>{weather.current.temp}°</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <div className={`rounded-2xl p-4 flex items-center gap-3 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}><div className="p-2 bg-sky-500/20 text-sky-500 rounded-lg"><FaTint /></div><div><p className="text-[10px] text-slate-500 font-bold uppercase"><T fr="Humidité" en="Humidity" /></p><p className={`text-lg font-black ${themeText}`}>{weather.current.humidity}%</p></div></div>
                      <div className={`rounded-2xl p-4 flex items-center gap-3 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}><div className="p-2 bg-sky-500/20 text-sky-500 rounded-lg"><FaWind /></div><div><p className="text-[10px] text-slate-500 font-bold uppercase"><T fr="Vent" en="Wind" /></p><p className={`text-lg font-black ${themeText}`}>{weather.current.wind} km/h</p></div></div>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
                      {weather.hourly?.time.map((time, i) => (i % 2 === 0 && i < 16) ? <div key={time} className={`min-w-[70px] rounded-xl p-3 text-center ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}><p className="text-[10px] text-slate-500 font-bold mb-1">{new Date(time).getHours()}h</p><p className={`text-base font-black ${themeText}`}>{Math.round(weather.hourly?.temp[i] || 0)}°</p></div> : null)}
                    </div>
                  </div>
                )}

                {/* WIDGET ROAD 511 */}
                {activeModal === "road_511" && (
                  <div className={`p-5 rounded-2xl border shadow-sm flex-shrink-0 ${isDarkMode ? 'bg-amber-900/20 border-amber-700/50' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center gap-3 mb-2"><FaExclamationTriangle className="text-amber-500 text-xl" /><p className={`font-black ${isDarkMode ? 'text-amber-400' : 'text-amber-800'}`}><T fr="Trafic Google Maps Actif" en="Google Maps Traffic Active" /></p></div>
                    <p className={`text-xs ${isDarkMode ? 'text-amber-200/70' : 'text-amber-700/80'}`}><T fr="Congestions et fermetures affichées en direct (Lignes rouges)." en="Congestion and closures shown live (red lines)." /></p>
                  </div>
                )}

                {/* LISTE DES RÉSULTATS */}
                {activeModal !== "weather_adv" && activeModal !== "satellite" && (
                  <div className="overflow-y-auto pr-2 space-y-3 flex-1 min-h-[200px]">
                    {isLoadingMapData ? (
                      <div className="flex justify-center p-10"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>
                    ) : activeData.length === 0 ? (
                      <div className={`text-center p-10 text-sm font-medium ${cardText}`}><T fr="Aucun résultat dans cette zone." en="No results in this area." /></div>
                    ) : (
                      activeData.filter(item => activeModal !== "roadtrip" || evFilter === 'all' || item.status === evFilter).map(item => (
                        <div key={item.id} onClick={() => { setMapCenter({lat: item.lat, lng: item.lng}); setSelectedMarker(item); }} className={`p-4 rounded-2xl cursor-pointer transition border ${isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-blue-500' : 'bg-white border-slate-100 shadow-sm hover:border-blue-300'}`}>
                          <div className="flex justify-between items-start mb-1">
                            <span className={`font-bold text-sm leading-tight pr-2 ${themeText}`}>{item.name}</span>
                            {item.distanceKm !== undefined && <span className={`text-[10px] font-black px-2 py-1 rounded-full whitespace-nowrap ${isDarkMode ? 'bg-slate-700 text-blue-400' : 'bg-blue-50 text-blue-800'}`}>{(item.distanceKm).toFixed(1)} km</span>}
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <span className={`text-[10px] font-black uppercase tracking-wider`} style={{ color: item.color }}>{item.type}</span>
                            <span className={`text-xs truncate max-w-[60%] ${cardText}`}>{item.details}</span>
                          </div>
                          
                          {activeModal === "transit" && (
                            <div className="flex items-center gap-2 mt-3">
                              <button onClick={(e) => { e.stopPropagation(); handleAskForfy(item); setMapCenter({lat: item.lat, lng: item.lng}); }} className={`flex-1 text-[10px] font-black py-2 rounded-xl transition flex items-center justify-center gap-2 ${isDarkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-slate-100 text-slate-800 hover:bg-slate-200'}`}><FaRobot /> <T fr="Forfy Statut" en="Forfy Status" /></button>
                              {item.link && <button onClick={(e) => { e.stopPropagation(); window.open(item.link, "_blank"); }} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black py-2 rounded-xl transition flex items-center justify-center gap-2 shadow-sm"><T fr="Site Officiel" en="Official Site" /></button>}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* FAQ SPÉCIFIQUE AU MODULE */}
                {MODAL_FAQS[activeModal] && (
                  <div className={`mt-auto border-t flex-shrink-0 pt-4 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                    <div className={`flex items-center gap-2 mb-3 text-xs font-black uppercase tracking-widest ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      <FaInfoCircle /> <T fr="Aide &amp; Conseils" en="Help &amp; Tips" />
                    </div>
                    <div className="space-y-2">
                      {MODAL_FAQS[activeModal].map((faq, index) => (
                        <div key={index} className={`rounded-xl overflow-hidden border transition ${isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-slate-100 bg-white'}`}>
                          <button className={`w-full px-4 py-3 flex items-center justify-between text-left text-[11px] font-bold ${themeText}`} onClick={() => toggleModalFaq(index)}>
                            <span className="pr-4">{faq.question}</span>
                            <FaChevronDown className={`transition-transform duration-300 flex-shrink-0 ${activeModalFaq === index ? "rotate-180" : ""}`} />
                          </button>
                          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${activeModalFaq === index ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                            <div className={`px-4 pb-4 font-medium text-[11px] leading-relaxed ${cardText}`}>
                              {faq.answer}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>

              {/* PANNEAU DROIT : CARTE GOOGLE MAPS */}
              <div className={`lg:col-span-8 p-2 rounded-3xl shadow-inner border relative min-h-[400px] flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                {isLoaded && (
                  <GoogleMap mapContainerStyle={{ width: "100%", height: "100%", borderRadius: "1.2rem", flex: 1 }} center={mapCenter} zoom={mapZoom} options={{ disableDefaultUI: true, zoomControl: true, styles: isDarkMode ? darkMapStyle : undefined }}>
                    
                    {activeModal === "road_511" && <TrafficLayer />}
                    {userLocation && <MarkerF position={userLocation} icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#3b82f6", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 }} title="Vous êtes ici" />}
                    
                    {activeData.map(item => (
                      <MarkerF key={item.id} position={{ lat: item.lat, lng: item.lng }} onClick={() => setSelectedMarker(item)} icon={getMarkerIcon(item.color, item.emoji)} />
                    ))}
                    
                    {selectedMarker && (
                      <InfoWindowF position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }} onCloseClick={() => setSelectedMarker(null)}>
                        <div className="p-2 min-w-[180px]">
                          <h3 className="font-bold text-slate-900 mb-1 leading-tight">{selectedMarker.name}</h3>
                          <p className="text-[10px] font-black uppercase mb-1" style={{ color: selectedMarker.color }}>{selectedMarker.type}</p>
                          <p className="text-xs text-slate-600 leading-snug">{selectedMarker.details}</p>
                          
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${selectedMarker.lat},${selectedMarker.lng}`} target="_blank" rel="noreferrer" className="mt-3 block text-center w-full bg-slate-900 text-white text-[11px] font-black py-2 rounded-lg uppercase transition shadow-sm">
                            <T fr="Itinéraire Maps" en="Maps Directions" />
                          </a>
                        </div>
                      </InfoWindowF>
                    )}
                  </GoogleMap>
                )}
                
                {/* AI FORFY TRANSIT */}
                {(aiLoading || aiResponse) && activeModal === "transit" && (
                  <div className={`absolute bottom-6 left-6 right-16 p-4 rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-slate-900/95 border-slate-700 text-white' : 'bg-slate-900/95 border-slate-800 text-white'} backdrop-blur-md`}>
                    <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-1 flex items-center gap-2"><FaRobot /> <T fr="Analyse Forfy Premium" en="Forfy Premium Analysis" /></div>
                    {aiLoading ? <p className="text-sm text-slate-300 font-medium italic animate-pulse"><T fr="L'Intelligence Artificielle analyse le site..." en="Artificial intelligence is analyzing the site..." /></p> : <p className="text-sm font-medium leading-relaxed">{aiResponse}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ GLOBALE PAGE D'ACCUEIL */}
      <div className="max-w-4xl mx-auto px-6 mt-10 relative z-10 mb-24">
        <div className="text-center mb-10"><h2 className={`text-3xl font-black mb-3 tracking-tight ${themeText}`}><T fr="Foire Aux Questions" en="Frequently Asked Questions" /></h2></div>
        <div className="space-y-4">
          {FAQS_GLOBALES.map((faq, index) => (
            <div key={index} className={`rounded-2xl overflow-hidden shadow-sm transition border ${cardBg}`}>
              <button className={`w-full px-6 py-5 flex items-center justify-between text-left transition-colors ${isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'}`} onClick={() => toggleFaq(index)}>
                <span className={`font-extrabold text-base ${themeText}`}>{faq.question}</span>
                <span className={`p-2 rounded-full transition-transform duration-300 ${activeFaq === index ? "rotate-180" : ""} ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-400'}`}>
                  <FaChevronDown />
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${activeFaq === index ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                <div className={`px-6 pb-6 font-medium leading-relaxed ${cardText}`}>
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
